import { useEffect, useRef, useState } from 'react';

import { MedicationStackService } from '../../services/medicationStackService';

/**
 * Keeps the Replenish recommendation in step with the user's medications.
 *
 * This behaviour used to live inside `RecommendedStacksTab.js`, the component
 * STACK-515 deletes. It is extracted rather than inlined into the new page for
 * one reason: **dropping it is a silent failure.** Nothing errors, nothing on
 * screen changes, and the card keeps rendering a stack built from a medication
 * list the user has since changed — which still looks authoritative. Tanu,
 * 2026-08-19: this is the core value of Replenish and Nourish, and a stack that
 * silently stops updating is worse than none.
 *
 * A hook with its own test is the shape that makes that non-silent: the test
 * fails if the poll is not started when a newer medication list exists.
 *
 * The cycle, unchanged from the tab it came from:
 *   1. Medications present, and either newer than the stack or no stack yet.
 *   2. If no stack exists at all, generate first.
 *   3. Poll for the update (30s / 2s).
 *   4. On timeout, regenerate once and poll again on a shorter cycle
 *      (15s / 1s). Give up after the second timeout.
 *
 * The 5s `lastGenerationAttempt` guard is also carried over: without it the
 * effect re-fires on every render that changes `medications`' identity and
 * stacks generate requests.
 */

export const GENERATE_THROTTLE_MS = 5000;
const POLL_TIMEOUT_MS = 30000;
const POLL_INTERVAL_MS = 2000;
const RETRY_TIMEOUT_MS = 15000;
const RETRY_INTERVAL_MS = 1000;
const RETRY_DELAY_MS = 2000;

export default function useRecommendedStackRefresh({
  medications,
  medicationStack,
  onStackUpdated,
  enabled = true,
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollingCleanupRef = useRef(null);
  const retryTimerRef = useRef(null);
  const lastGenerationAttempt = useRef(0);
  /**
   * The in-flight guard is a ref, not the `isRefreshing` state, and the
   * unmount guard is a ref too. Both were state/per-effect flags first, and
   * that was wrong in a way worth recording, because it failed silently:
   *
   * `isRefreshing` in the dependency array meant `setIsRefreshing(true)`
   * re-ran the effect, whose cleanup then flipped the *previous* run's
   * cancellation flag — the one the live poll's callbacks close over. The poll
   * still ran, still succeeded, and its "we are done" callback did nothing.
   * The card would have sat in its generating state forever while a perfectly
   * good stack sat behind it.
   *
   * A ref does not re-run the effect, so the guard guards and the cleanup only
   * fires on a real unmount or a real dependency change.
   */
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingCleanupRef.current) pollingCleanupRef.current();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const medicationCount = Array.isArray(medications) ? medications.length : 0;
    if (!enabled || medicationCount === 0 || inFlightRef.current) return;
    if (Date.now() - lastGenerationAttempt.current < GENERATE_THROTTLE_MS) return;

    const finish = () => {
      inFlightRef.current = false;
      if (!mountedRef.current) return;
      setIsRefreshing(false);
    };

    const run = async () => {
      try {
        const isNewer = await MedicationStackService.isMedicationListNewer();
        // Two ways in: the list moved on, or there is nothing built yet.
        const shouldStart = isNewer || !medicationStack;
        if (!shouldStart || !mountedRef.current) return;

        lastGenerationAttempt.current = Date.now();
        inFlightRef.current = true;
        setIsRefreshing(true);

        if (!medicationStack) {
          const meds = await MedicationStackService.getUserMedications();
          await MedicationStackService.generateStack(meds);
        }

        pollingCleanupRef.current = MedicationStackService.pollForStackUpdate(
          () => {
            finish();
            onStackUpdated?.();
          },
          async () => {
            try {
              const meds = await MedicationStackService.getUserMedications();
              const success = await MedicationStackService.generateStack(meds);
              if (!success) {
                finish();
                return;
              }
              retryTimerRef.current = setTimeout(() => {
                pollingCleanupRef.current = MedicationStackService.pollForStackUpdate(
                  () => {
                    finish();
                    onStackUpdated?.();
                  },
                  // Second timeout: stop. The card stays in its generating
                  // state rather than claiming a stack that never arrived.
                  finish,
                  RETRY_TIMEOUT_MS,
                  RETRY_INTERVAL_MS
                );
              }, RETRY_DELAY_MS);
            } catch (error) {
              console.error('[Dashboard] Error regenerating medication stack:', error);
              finish();
            }
          },
          POLL_TIMEOUT_MS,
          POLL_INTERVAL_MS
        );
      } catch (error) {
        console.error('[Dashboard] Error checking for medication stack updates:', error);
        finish();
      }
    };

    run();
  }, [medications, medicationStack, onStackUpdated, enabled]);

  return isRefreshing;
}
